import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
const SCAN_COUNT_KEY = 'google_review_scan_count';
const REVIEW_SHOWN_KEY = 'google_review_shown';
const REVIEW_THRESHOLD = 5;
interface GoogleReviewContextType {
  incrementScanCount: () => void;
  scanCount: number;
  isModalOpen: boolean;
  closeModal: () => void;
}
const GoogleReviewContext = createContext<GoogleReviewContextType>({
  incrementScanCount: () => {},
  scanCount: 0,
  isModalOpen: false,
  closeModal: () => {}
});
export function useGoogleReview() {
  return useContext(GoogleReviewContext);
}
interface GoogleReviewProviderProps {
  children: ReactNode;
}
export function GoogleReviewProvider({
  children
}: GoogleReviewProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scanCount, setScanCount] = useState(() => {
    return parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
  });
  const triggerReviewIfNeeded = useCallback((newCount: number) => {
    const alreadyShown = localStorage.getItem(REVIEW_SHOWN_KEY) === 'true';
    console.log(`[GoogleReview] Count: ${newCount}, alreadyShown: ${alreadyShown}, threshold: ${REVIEW_THRESHOLD}`);
    if (newCount >= REVIEW_THRESHOLD && !alreadyShown) {
      setTimeout(() => {
        setIsModalOpen(true);
        console.log('[GoogleReview] Modal opened!');
      }, 1500);
    }
  }, []);
  // Check on mount if we should show the modal
  useEffect(() => {
    const count = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
    triggerReviewIfNeeded(count);
  }, [triggerReviewIfNeeded]);
  // Listen for custom 'scanCompleted' events dispatched by GoogleReviewTrigger
  useEffect(() => {
    const handleScanCompleted = () => {
      const currentCount = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
      const newCount = currentCount + 1;
      localStorage.setItem(SCAN_COUNT_KEY, String(newCount));
      setScanCount(newCount);
      console.log(`[GoogleReview] scanCompleted event received. New count: ${newCount}`);
      triggerReviewIfNeeded(newCount);
    };
    window.addEventListener('scanCompleted', handleScanCompleted);
    return () => window.removeEventListener('scanCompleted', handleScanCompleted);
  }, [triggerReviewIfNeeded]);
  const incrementScanCount = useCallback(() => {
    const currentCount = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
    const newCount = currentCount + 1;
    localStorage.setItem(SCAN_COUNT_KEY, String(newCount));
    setScanCount(newCount);
    triggerReviewIfNeeded(newCount);
  }, [triggerReviewIfNeeded]);
  const handleClose = useCallback(() => {
    localStorage.setItem(REVIEW_SHOWN_KEY, 'true');
    setIsModalOpen(false);
  }, []);
  return <GoogleReviewContext.Provider value={{
    incrementScanCount,
    scanCount,
    isModalOpen,
    closeModal: handleClose
  }} data-id="element-3424">
      {children}
    </GoogleReviewContext.Provider>;
}